/**
 * Génère une image « Ma Transformation » brandée Body-Up (avant/après côte à côte)
 * prête à partager sur Instagram/WhatsApp. Format portrait 4:5 (1080x1350).
 * Les photos viennent d'object URLs same-origin (blob:) -> pas de taint canvas.
 */

import QRCode from 'qrcode';

const W = 1080;
const H = 1350;

const TRANSFORMATION_URL = 'https://body-up.ch/transformation';

const loadImg = (url) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });

const drawCover = (ctx, img, x, y, w, h) => {
  const ir = img.width / img.height;
  const r = w / h;
  let sw, sh, sx, sy;
  if (ir > r) {
    sh = img.height;
    sw = sh * r;
    sx = (img.width - sw) / 2;
    sy = 0;
  } else {
    // Photo plein corps plus haute que le cadre : on recadre en priorité par le bas
    // (visage jamais coupé) plutôt qu'un centrage strict qui rogne souvent le haut de la tête.
    sw = img.width;
    sh = sw / r;
    sx = 0;
    sy = (img.height - sh) * 0.18;
  }
  ctx.save();
  const rad = 24;
  ctx.beginPath();
  ctx.moveTo(x + rad, y);
  ctx.arcTo(x + w, y, x + w, y + h, rad);
  ctx.arcTo(x + w, y + h, x, y + h, rad);
  ctx.arcTo(x, y + h, x, y, rad);
  ctx.arcTo(x, y, x + w, y, rad);
  ctx.closePath();
  ctx.clip();
  ctx.drawImage(img, sx, sy, sw, sh, x, y, w, h);
  ctx.restore();
};

const drawTag = (ctx, label, date, x, y) => {
  ctx.font = '800 30px Outfit, Arial';
  const tw = ctx.measureText(label).width + 36;
  ctx.fillStyle = 'rgba(0,0,0,0.62)';
  ctx.beginPath();
  ctx.roundRect(x, y, tw, 48, 12);
  ctx.fill();
  ctx.fillStyle = '#fff';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.fillText(label, x + 18, y + 25);
  if (date) {
    ctx.font = '600 22px Manrope, Arial';
    ctx.fillStyle = 'rgba(255,255,255,0.9)';
    ctx.fillStyle = 'rgba(0,0,0,0.62)';
    const dw = ctx.measureText(date).width + 28;
    ctx.beginPath();
    ctx.roundRect(x, y + 56, dw, 38, 10);
    ctx.fill();
    ctx.fillStyle = 'rgba(255,255,255,0.92)';
    ctx.fillText(date, x + 14, y + 76);
  }
};

export async function buildTransformationImage({ beforeUrl, afterUrl, beforeDate, afterDate, wDelta, en }) {
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');

  // Fond
  ctx.fillStyle = '#0a0a0a';
  ctx.fillRect(0, 0, W, H);

  // Bandeau de marque
  const hg = ctx.createLinearGradient(0, 0, W, 0);
  hg.addColorStop(0, '#E91E63');
  hg.addColorStop(1, '#EC4899');
  ctx.fillStyle = hg;
  ctx.fillRect(0, 0, W, 172);
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'left';
  ctx.fillStyle = '#fff';
  ctx.font = '800 66px Outfit, Arial';
  ctx.fillText('BODY-UP', 56, 72);
  ctx.font = '600 24px Manrope, Arial';
  ctx.fillStyle = 'rgba(255,255,255,0.88)';
  ctx.fillText('PERSONAL TRAINING · NYON', 60, 122);
  ctx.textAlign = 'right';
  ctx.fillStyle = '#fff';
  ctx.font = '800 32px Outfit, Arial';
  ctx.fillText(en ? 'MY TRANSFORMATION' : 'MA TRANSFORMATION', W - 56, 86);

  // Photos avant / après
  const top = 220;
  const gap = 24;
  const side = 44;
  const colW = (W - side * 2 - gap) / 2;
  const colH = 840;
  try {
    const [imgB, imgA] = await Promise.all([loadImg(beforeUrl), loadImg(afterUrl)]);
    drawCover(ctx, imgB, side, top, colW, colH);
    drawCover(ctx, imgA, side + colW + gap, top, colW, colH);
  } catch {
    // si une image manque, on stoppe proprement
    throw new Error('image_load_failed');
  }
  drawTag(ctx, en ? 'BEFORE' : 'AVANT', beforeDate, side + 18, top + 18);
  drawTag(ctx, en ? 'AFTER' : 'APRÈS', afterDate, side + colW + gap + 18, top + 18);

  // Badge variation de poids
  if (wDelta != null) {
    const txt = `${wDelta > 0 ? '+' : ''}${wDelta} kg`;
    ctx.font = '800 40px Outfit, Arial';
    const bw = ctx.measureText(txt).width + 90;
    const bx = (W - bw) / 2;
    const by = top + colH + 28;
    ctx.fillStyle = wDelta < 0 ? '#10B981' : wDelta > 0 ? '#EC4899' : '#52525b';
    ctx.beginPath();
    ctx.roundRect(bx, by, bw, 70, 35);
    ctx.fill();
    ctx.fillStyle = '#fff';
    ctx.textAlign = 'center';
    ctx.font = '800 40px Outfit, Arial';
    ctx.fillText(txt, W / 2, by + 36);
  }

  // Pied de page : coordonnées + QR code vers le coaching
  const fy = H - 138;
  ctx.fillStyle = 'rgba(255,255,255,0.06)';
  ctx.fillRect(0, fy - 22, W, 160);

  // QR code (vers /transformation) à droite
  let qrImg = null;
  try {
    const qrDataUrl = await QRCode.toDataURL(TRANSFORMATION_URL, {
      margin: 1, width: 240, color: { dark: '#0a0a0a', light: '#ffffff' },
    });
    qrImg = await loadImg(qrDataUrl);
  } catch {
    qrImg = null;
  }
  const qrSize = 118;
  const qrX = W - 56 - qrSize;
  const qrY = fy - 8;
  if (qrImg) {
    ctx.fillStyle = '#fff';
    ctx.beginPath();
    ctx.roundRect(qrX - 8, qrY - 8, qrSize + 16, qrSize + 16, 14);
    ctx.fill();
    ctx.drawImage(qrImg, qrX, qrY, qrSize, qrSize);
    ctx.fillStyle = 'rgba(255,255,255,0.92)';
    ctx.font = '700 19px Manrope, Arial';
    ctx.textAlign = 'center';
    ctx.fillText(en ? 'Coach with Laurent' : 'Coache avec Laurent', qrX + qrSize / 2, qrY + qrSize + 22);
  }

  // Coordonnées (alignées à gauche pour laisser la place au QR)
  ctx.textAlign = 'left';
  ctx.fillStyle = '#fff';
  ctx.font = '700 27px Manrope, Arial';
  ctx.fillText('Tél +41 78 604 44 23  ·  info@body-up.ch', 56, fy + 18);
  ctx.font = '600 23px Manrope, Arial';
  ctx.fillStyle = 'rgba(255,255,255,0.82)';
  ctx.fillText("Route des Tattes d'Oie 95, 1260 Nyon", 56, fy + 56);
  ctx.fillStyle = '#EC4899';
  ctx.font = '800 25px Outfit, Arial';
  ctx.fillText('body-up.ch', 56, fy + 96);

  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.92));
}
